import { Chip } from './primitives/Chip';
import { Section } from './primitives/Section';
import { MonoKicker } from './primitives/MonoKicker';

const steps = [
    {
        title: 'Listens',
        body: 'Runs ambiently on your phone or desktop. No wake words, no dictation voice. Just talk to your client.',
    },
    {
        title: 'Drafts',
        body: 'A structured note in your format, in your voice. You review, edit, and sign — and it learns from every edit.',
    },
    {
        title: 'Files',
        body: 'The part everyone else skips. Signed notes route to the right chart, folder and follow-up queue automatically.',
    },
];

/**
 * Deliberately bespoke and long. Every element of the note preview appears
 * exactly once, so factoring it into a component would produce nine props and
 * a single caller.
 *
 * The SOAP labels use `--primary` rather than `--brand-terracotta`: they are
 * 14px, where the bright terracotta is 2.91:1 on cream.
 */
export function ScribeSection() {
    return (
        <Section id="what" className="border-b border-border">
            <div className="flex flex-col gap-12">
                <div className="flex max-w-3xl flex-col gap-4">
                    <MonoKicker data-reveal>Build 01 · the scribe</MonoKicker>
                    <h2
                        data-reveal
                        className="font-display text-[clamp(30px,4vw,40px)] leading-[1.15] text-balance"
                    >
                        Pajama-time charting: cancelled.
                    </h2>
                    <p
                        data-reveal
                        className="text-[clamp(16px,1.4vw,18px)] leading-[1.55] text-muted-foreground"
                    >
                        Every engagement opens here, because notes are the
                        fastest hour to hand back. Ambient capture during the
                        visit, a structured draft in your format before you
                        reach the door handle, and filing that happens without
                        you.
                    </p>
                </div>

                <div className="grid grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-8">
                    {steps.map((step) => (
                        <div
                            key={step.title}
                            data-reveal
                            className="flex flex-col gap-2"
                        >
                            <h3 className="font-display text-[26px] leading-[1.2] text-primary">
                                {step.title}
                            </h3>
                            <p className="text-[16px] leading-[1.55] text-muted-foreground">
                                {step.body}
                            </p>
                        </div>
                    ))}
                </div>

                <div
                    data-reveal
                    className="rounded-[16px] border border-border bg-card p-[clamp(20px,3vw,32px)]"
                >
                    <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-border pb-0">
                        <MonoKicker className="pb-4">
                            Draft · visit 14:00–14:50
                        </MonoKicker>
                        <Chip shape="tab" tone="sage" className="-mb-px">
                            Ready to sign
                        </Chip>
                    </div>

                    <div className="flex flex-col gap-3 font-mono text-[13px] leading-[1.6]">
                        <p>
                            <span className="text-primary">S:</span> 54yo M
                            presents for f/u of HTN. Reports good adherence,
                            occasional AM headaches.
                        </p>
                        <p>
                            <span className="text-primary">O:</span> BP 138/84.
                            HR 72. Lungs CTA. No LE edema.
                        </p>
                        <p>
                            <span className="text-primary">A:</span> HTN,
                            improving on current regimen.
                        </p>
                        <p>
                            <span className="text-primary">P:</span> Continue
                            lisinopril 20mg. Recheck 3 mo.{' '}
                            <mark className="bg-brand-mustard/40 text-foreground">
                                Patient to log home BP twice weekly.
                            </mark>
                        </p>
                    </div>
                </div>
            </div>
        </Section>
    );
}
